(ns app.front.view.profile.subs
  (:require [app.front.state-machine :as sm]
            [app.front.validate :as validate]
            [app.front.view.profile.licences :as profile-licences]
            [clojure.string :as str]
            [goog.functions]
            [potpuri.core :as p]
            [re-frame.core-instrumented :as rf]
            [schema.core :as s]))

(def model {:user/first-name     [[validate/NonBlankString :validate/blank-first-name]]
            :user/last-name      [[validate/NonBlankString :validate/blank-last-name]]
            :user/street-address [[validate/NonBlankString :validate/blank-street-address]]
            :user/unit-number    [[s/Any ""]] ;; optional
            :user/lot-con        [[s/Any ""]] ;; optional
            :user/municipality   [[validate/NonBlankString :validate/blank-municipality]]
            :user/province       [[validate/NonBlankString :validate/blank-province]]
            :user/postal-code    [[validate/NonBlankString :validate/blank-postal-code]]
            :user/email          [[validate/Email :validate/wrong-email-address]]
            :user/mobile-phone   [[validate/Phone :validate/wrong-mobile-phone-format]]
            :user/other-phone    [[validate/BlankOrPhone :validate/wrong-phone-format]] ;; optional
            :user/fax            [[validate/BlankOrPhone :validate/wrong-fax-format]]}) ;; optional

(def model-authority-user {:user/first-name   [[validate/NonBlankString :validate/blank-first-name]]
                           :user/last-name    [[validate/NonBlankString :validate/blank-last-name]]
                           :user/unit-number  [[s/Any ""]] ;; optional
                           :user/postal-code  [[s/Any ""]] ;; optional
                           :user/email        [[validate/Email :validate/wrong-email-address]]
                           :user/mobile-phone [[validate/BlankOrPhone :validate/wrong-mobile-phone-format]]
                           :user/other-phone  [[validate/BlankOrPhone :validate/wrong-phone-format]] ;; optional
                           :user/fax          [[validate/BlankOrPhone :validate/wrong-fax-format]]}) ;; optional

(defn dynamic-model [db]
  (let [fields (::form-data db)]
    {:user/corporation-or-partnership ;; optional or required based on :corporation-checked
     [(if (:corporation-checked fields)
        [validate/NonBlankString :validate/blank-corporation]
        [s/Any ""])]}))

(defn get-model [db]
  (if (-> db :session/user :user/authority?)
    model-authority-user
    (merge model (dynamic-model db))))

(def initial-form-data {:user/first-name                 nil
                        :user/last-name                  nil
                        :user/corporation-or-partnership nil ;; optional
                        :user/street-address             nil
                        :user/unit-number                nil ;; optional
                        :user/lot-con                    nil ;; optional
                        :user/municipality               nil
                        :user/province                   nil
                        :user/postal-code                nil
                        :user/mobile-phone               nil
                        :user/other-phone                nil ;; optional
                        :user/fax                        nil ;; optional

                        :user/current-password nil
                        :user/new-password     nil

                        :user/email nil

                        ::state               nil
                        ::corporation-checked false
                        ::errors              {}
                        ::server-errors       nil})

(def state-machine
  {nil                 {::init ::no-profile}
   ::no-profile        {::fetch-profile ::ready}
   ::ready             {::set-form-data ::client-validation}
   ::client-validation {::try-submit   ::submitting
                        ::still-errors ::client-validation}
   ::submitting        {::submit-errors  ::server-errors
                        ::submit-success ::ready}
   ::server-errors     {::set-form-data ::client-validation}})


;; -- Subscriptions -----------------------------------------------------------

(rf/reg-sub ::form-data
  (fn [db [_ path]]
    (let [form-data (::form-data db)]
      (if path (get-in form-data path) form-data))))

(rf/reg-sub ::state
  :<- [::form-data]
  :-> ::state)

(rf/reg-sub ::show-errors?
  :<- [::form-data]
  :-> ::show-errors?)

(rf/reg-sub ::server-errors
  :<- [::form-data]
  :-> ::server-errors)

(rf/reg-sub ::corporation-checked
  :<- [::form-data]
  :-> :corporation-checked)

(rf/reg-sub ::pending?
  :<- [::state]
  (fn [state _]
    (or (= state nil) (= state ::no-profile))))

(rf/reg-sub ::model
  :-> get-model)

(rf/reg-sub ::context
  :-> ::context)

(rf/reg-sub ::oidc-suggestion
  :<- [::context]
  :-> ::oidc-suggestion)

(rf/reg-sub ::mfa-suggestions
  :<- [::context]
  :-> ::mfa-suggestions)

;; -- Events ------------------------------------------------------------------

(def update-next-state (sm/make-update-next-state state-machine [::form-data ::state]))

(def debug
  (rf/after
    (fn [db event]
      (js/console.debug "=======")
      (js/console.debug "state: " (str (-> db ::form-data ::state)))
      (js/console.debug "event: " (str event)))))

(def interceptors [(rf/inject-cofx :tr) (when ^boolean js/goog.DEBUG debug)])

(rf/reg-event-fx ::set-form-data
  interceptors
  (fn [{:keys [db]} [event path value]]
    {:db       (-> db
                   (assoc-in (into [::form-data] path) value)
                   (update-next-state event))
     :dispatch [::validate-fields]}))

(rf/reg-event-fx ::validate-fields
  interceptors
  (fn [{:keys [db]} _]
    (if (empty? (validate/validate-form (::form-data db) (get-model db)))
      {:dispatch-debounce [{:id      ::try-submit-debounce
                            :timeout 1000
                            :action  :dispatch
                            :event   [::try-submit (::form-data db)]}]}
      {:dispatch-debounce [{:id     ::try-submit-debounce
                            :action :cancel}]
       :dispatch          [::still-errors]})))

(rf/reg-event-fx ::try-submit
  interceptors
  (fn [{:keys [db]} [event profile]]
    (let [profile (cond-> profile
                    (-> profile :corporation-checked not) (assoc :user/corporation-or-partnership ""))]
      {:db           (update-next-state db event)
       :comm/command {:command        :user/save-profile
                      :data           (-> profile
                                          (select-keys [:user/first-name
                                                        :user/last-name
                                                        :user/corporation-or-partnership
                                                        :user/street-address
                                                        :user/unit-number
                                                        :user/lot-con
                                                        :user/municipality
                                                        :user/province
                                                        :user/postal-code
                                                        :user/mobile-phone
                                                        :user/other-phone
                                                        :user/fax
                                                        :user/professional
                                                        :user/speciality-area
                                                        :user/company-name
                                                        :user/notification-types])
                                          (#(p/remove-vals str/blank? %)))
                      :on-bad-request [::try-submit.failure]
                      :on-success     [::try-submit.success]}})))

(rf/reg-event-fx ::try-submit.success
  interceptors
  (fn [{:keys [tr db]} [event _]]
    {:db         (update-next-state db event)
     :dispatch-n [[:core.front.session/restore-session]
                  [:notifications/add
                   :toast
                   :success
                   (tr [:user/changes-saved])
                   (tr [:user/continue-editing])]]}))

(rf/reg-event-fx ::try-submit.failure
  interceptors
  (fn [{:keys [tr db]} [_ status body id]]
    (js/console.error "::submit-failure" status body)
    {:db db
     :fx [[:dispatch [::submit-errors]]
          [:dispatch [:notifications/add
                      :toast
                      :error
                      :error/error
                      (tr [:profile/update-failed] [id])]]]}))

(rf/reg-event-fx ::fetch-profile
  interceptors
  (fn [_ _]
    {:comm/command {:command        :user/fetch-profile
                    :method         :get
                    :on-bad-request [::fetch-profile-failure]
                    :on-success     [::fetch-profile.success]}}))

(rf/reg-event-fx ::fetch-profile.success
  (fn [{:keys [db]} [_ _ {:keys [profile oidc-suggestion licences mfa-suggestions]}]]
    {:db       (-> db
                   (update ::form-data merge profile)
                   (assoc-in [::form-data :corporation-checked] (-> profile
                                                                    :user/corporation-or-partnership
                                                                    str/blank?
                                                                    not))
                   (update ::context
                           assoc
                           ::oidc-suggestion oidc-suggestion
                           ::mfa-suggestions mfa-suggestions)
                   (update-next-state ::fetch-profile))
     :dispatch [::profile-licences/set-user-licences licences]}))

(rf/reg-event-fx ::init
  interceptors
  (fn [{:keys [db]} [event]]
    {:db (-> db
             (assoc ::form-data initial-form-data)
             (update-next-state event))}))

(rf/reg-event-fx ::fetch-profile-failure
  interceptors
  (fn [_ [_ status body]]
    (js/console.error "::fetch-profile-failure" status body)))

(rf/reg-event-db ::still-errors interceptors update-next-state)
(rf/reg-event-db ::submit-errors interceptors update-next-state)
